// 面板標題（ContentPanel 的 `{uiId}-heading`）在舊系統用的是第幾級標題。
//
// 【為什麼需要這張表】
// React 雛形是 7 個頁型模板共用，模板一律輸出同一級標題；但舊系統的層級是逐頁的
// JSP 各自寫死的。U4 量到 143/143 頁不符，根因就是模板固定用 <h2>。
//
// 【證據】量自 .tmp/ui/raw-dom/legacy/admin/**.html（143 頁，Playwright 擷取的實際 DOM），
// 只看 normalize-dom.ts R1 scope 內、且落在 `.ibox-title` 裡的標題（那才是面板標題）：
//
//     h5   89 個標題（85 頁各 1 + 2 頁各 2）   ← 97.8%
//     h4    2 個標題（2 頁）
//
// 與原始碼一致：CrudListPage.tsx 檔頭註解已記載 `.ibox-title > h5`（§7 第 3 級證據）。
//
// 【為什麼不是 route manifest 的欄位】routes.generated.ts 由 scripts/build-routes.ts
// 從 docs/legacy-candidates.json 產生、檔頭明寫「不要手改」；要加欄位得去改 Gate 1 的
// 盤點來源檔，等於把 HTML 標題層級這種渲染細節塞進盤點清單。例外只有 3 筆（143 頁的 2%），
// 分散到 143 筆 JSON 反而讓「預設是 h5」這個量測結論看不出來。
//
// 【為什麼不用 pageType 歸納】兩個 h4 的例外都是 crud-list，而另外 38 個 crud-list 是 h5；
// h5 本身又橫跨 crud-list / display / report / complex-form。層級與 pageType 無關。

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

/** 舊系統 `.ibox-title` 的多數形態（89/91）。沒有逐頁證據時一律用這個。 */
export const DEFAULT_PANEL_HEADING_LEVEL: HeadingLevel = 5;

/**
 * 有逐頁證據、且與預設不同的頁面。每一筆都要寫得出量到什麼。
 * 新增前請先量 .tmp/ui/raw-dom/legacy 的實際 DOM，不要憑印象加。
 */
export const PANEL_HEADING_LEVEL_OVERRIDES: Readonly<Record<string, HeadingLevel>> = {
  // raw-dom 實測：div.ibox-title > h4「公告」
  'announcement-announcement-list': 4,
  // raw-dom 實測：div.ibox-title > h4「組織維護」
  'config-organization': 4,
  // 登入頁沒有 .ibox-title，系統標題在 div.middle-box.loginscreen 內的 h3。
  // 與 LoginPage.tsx 檔頭註解「:79 `<h3>${sysTitle}</h3>`」一致。
  index: 3,
};

/** 這一頁的面板標題該用第幾級。查無逐頁證據就回 DEFAULT_PANEL_HEADING_LEVEL。 */
export function panelHeadingLevelFor(pageId: string): HeadingLevel {
  return PANEL_HEADING_LEVEL_OVERRIDES[pageId] ?? DEFAULT_PANEL_HEADING_LEVEL;
}
